const OUTPUT_MAX_WIDTH = 1280;

export type MeetingRecordingStream = MediaStream & {
  stopCapture: () => void;
};

function isVideoVisible(video: HTMLVideoElement): boolean {
  if (video.readyState < 2 || video.videoWidth === 0) return false;
  const rect = video.getBoundingClientRect();
  if (rect.width < 8 || rect.height < 8) return false;
  const style = window.getComputedStyle(video);
  if (style.display === 'none' || style.visibility === 'hidden') return false;
  if (Number(style.opacity) < 0.05) return false;
  return true;
}

function waitForVideos(container: HTMLElement, timeoutMs = 4000): Promise<HTMLVideoElement[]> {
  return new Promise((resolve, reject) => {
    const started = Date.now();

    const attempt = () => {
      const videos = Array.from(container.querySelectorAll('video')).filter(isVideoVisible);
      if (videos.length > 0) {
        resolve(videos);
        return;
      }
      if (Date.now() - started >= timeoutMs) {
        reject(new Error('NO_VIDEO'));
        return;
      }
      requestAnimationFrame(attempt);
    };

    attempt();
  });
}

function collectAudioStreams(
  videos: HTMLVideoElement[],
  extraStreams: MediaStream[]
): MediaStream[] {
  const streams: MediaStream[] = [];
  const seen = new Set<MediaStream>();

  const add = (stream: MediaStream | null | undefined) => {
    if (!stream || seen.has(stream)) return;
    if (stream.getAudioTracks().length === 0) return;
    seen.add(stream);
    streams.push(stream);
  };

  extraStreams.forEach(add);
  videos.forEach((video) => add(video.srcObject as MediaStream | null));

  return streams;
}

function mixAudioStreams(audioStreams: MediaStream[]): MediaStreamTrack[] {
  if (audioStreams.length === 0) return [];

  const audioCtx = new AudioContext();
  const destination = audioCtx.createMediaStreamDestination();

  audioStreams.forEach((stream) => {
    try {
      const source = audioCtx.createMediaStreamSource(stream);
      source.connect(destination);
    } catch {
      /* track may already be consumed */
    }
  });

  (destination.stream as MediaStream & { _audioCtx?: AudioContext })._audioCtx = audioCtx;

  return destination.stream.getAudioTracks();
}

type MappedRect = { x: number; y: number; w: number; h: number };

function mapRect(
  rect: DOMRect,
  containerRect: DOMRect,
  outW: number,
  outH: number
): MappedRect {
  const scaleX = outW / containerRect.width;
  const scaleY = outH / containerRect.height;
  return {
    x: (rect.left - containerRect.left) * scaleX,
    y: (rect.top - containerRect.top) * scaleY,
    w: rect.width * scaleX,
    h: rect.height * scaleY,
  };
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
) {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}

function isMirrored(video: HTMLVideoElement): boolean {
  const transform = window.getComputedStyle(video).transform;
  return transform !== 'none' && transform.includes('-1');
}

function drawObjectFit(
  ctx: CanvasRenderingContext2D,
  video: HTMLVideoElement,
  x: number,
  y: number,
  w: number,
  h: number,
  objectFit: string
) {
  const vw = video.videoWidth;
  const vh = video.videoHeight;
  if (!vw || !vh) return;

  if (objectFit === 'contain') {
    const scale = Math.min(w / vw, h / vh);
    const dw = vw * scale;
    const dh = vh * scale;
    ctx.drawImage(video, x + (w - dw) / 2, y + (h - dh) / 2, dw, dh);
    return;
  }

  const videoRatio = vw / vh;
  const rectRatio = w / h;
  let sx = 0;
  let sy = 0;
  let sw = vw;
  let sh = vh;

  if (videoRatio > rectRatio) {
    sw = vh * rectRatio;
    sx = (vw - sw) / 2;
  } else {
    sh = vw / rectRatio;
    sy = (vh - sh) / 2;
  }

  ctx.drawImage(video, sx, sy, sw, sh, x, y, w, h);
}

function paintTileBackground(
  ctx: CanvasRenderingContext2D,
  mapped: MappedRect,
  bg: string | null
) {
  const radius = Math.min(16, mapped.w * 0.06, mapped.h * 0.06);

  ctx.save();
  roundRect(ctx, mapped.x, mapped.y, mapped.w, mapped.h, radius);
  ctx.clip();

  if (bg === 'purple') {
    const gradient = ctx.createLinearGradient(mapped.x, mapped.y, mapped.x + mapped.w, mapped.y + mapped.h);
    gradient.addColorStop(0, '#6b21a8');
    gradient.addColorStop(1, '#0f172a');
    ctx.fillStyle = gradient;
  } else if (bg === 'blue') {
    const gradient = ctx.createLinearGradient(mapped.x, mapped.y, mapped.x + mapped.w, mapped.y + mapped.h);
    gradient.addColorStop(0, '#1d4ed8');
    gradient.addColorStop(1, '#0f172a');
    ctx.fillStyle = gradient;
  } else if (bg === 'blur') {
    ctx.fillStyle = '#475569';
  } else if (bg === 'transparent') {
    ctx.fillStyle = '#0f172a';
  } else if (bg === 'black') {
    ctx.fillStyle = '#000000';
  } else {
    ctx.fillStyle = '#1e293b';
  }

  ctx.fillRect(mapped.x, mapped.y, mapped.w, mapped.h);
  ctx.restore();
}

function paintStripChrome(
  ctx: CanvasRenderingContext2D,
  strip: HTMLElement,
  containerRect: DOMRect,
  outW: number,
  outH: number
) {
  const mapped = mapRect(strip.getBoundingClientRect(), containerRect, outW, outH);
  const radius = 12;

  ctx.save();
  roundRect(ctx, mapped.x, mapped.y, mapped.w, mapped.h, radius);
  ctx.fillStyle = 'rgba(15, 23, 42, 0.92)';
  ctx.fill();
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.15)';
  ctx.lineWidth = 1;
  ctx.stroke();
  ctx.restore();
}

function drawPreviewFrame(
  ctx: CanvasRenderingContext2D,
  container: HTMLElement,
  videos: HTMLVideoElement[],
  outW: number,
  outH: number
) {
  const containerRect = container.getBoundingClientRect();
  if (containerRect.width < 1 || containerRect.height < 1) return;

  ctx.fillStyle = '#000000';
  ctx.fillRect(0, 0, outW, outH);

  container.querySelectorAll('[data-recording-tile]').forEach((tile) => {
    const element = tile as HTMLElement;
    const mapped = mapRect(element.getBoundingClientRect(), containerRect, outW, outH);
    paintTileBackground(ctx, mapped, element.getAttribute('data-recording-bg'));
  });

  const strip = container.querySelector('[data-recording-strip]');
  if (strip instanceof HTMLElement) {
    paintStripChrome(ctx, strip, containerRect, outW, outH);
  }

  videos.forEach((video) => {
    const rect = video.getBoundingClientRect();
    const mapped = mapRect(rect, containerRect, outW, outH);
    if (mapped.w < 1 || mapped.h < 1) return;

    const style = window.getComputedStyle(video);
    const objectFit = style.objectFit || 'cover';
    const filter = style.filter;
    const opacity = Number(style.opacity);
    const clipTile = video.closest('[data-recording-tile]') as HTMLElement | null;

    ctx.save();

    if (clipTile) {
      const clipMapped = mapRect(clipTile.getBoundingClientRect(), containerRect, outW, outH);
      const radius = Math.min(16, clipMapped.w * 0.06, clipMapped.h * 0.06);
      roundRect(ctx, clipMapped.x, clipMapped.y, clipMapped.w, clipMapped.h, radius);
      ctx.clip();
    }

    if (filter && filter !== 'none') {
      ctx.filter = filter;
    }

    if (opacity < 1) {
      ctx.globalAlpha = opacity;
    }

    if (isMirrored(video)) {
      ctx.translate(mapped.x + mapped.w, mapped.y);
      ctx.scale(-1, 1);
      drawObjectFit(ctx, video, 0, 0, mapped.w, mapped.h, objectFit);
    } else {
      drawObjectFit(ctx, video, mapped.x, mapped.y, mapped.w, mapped.h, objectFit);
    }

    ctx.restore();
  });
}

export async function createMeetingRecordingStream(
  container: HTMLElement,
  extraAudioStreams: MediaStream[] = []
): Promise<MeetingRecordingStream> {
  const videos = await waitForVideos(container);

  const containerRect = container.getBoundingClientRect();
  const outputWidth = OUTPUT_MAX_WIDTH;
  const outputHeight = Math.max(
    1,
    Math.round((containerRect.height / Math.max(containerRect.width, 1)) * outputWidth)
  );

  const canvas = document.createElement('canvas');
  canvas.width = outputWidth;
  canvas.height = outputHeight;

  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('NO_CANVAS');

  let rafId = 0;
  const draw = () => {
    const currentVideos = Array.from(container.querySelectorAll('video')).filter(isVideoVisible);
    drawPreviewFrame(ctx, container, currentVideos, outputWidth, outputHeight);
    rafId = requestAnimationFrame(draw);
  };
  draw();

  const canvasStream = canvas.captureStream(30);
  const audioTracks = mixAudioStreams(collectAudioStreams(videos, extraAudioStreams));

  const tracks = [
    ...canvasStream.getVideoTracks(),
    ...audioTracks.map((track) => track.clone()),
  ];

  const mixed = new MediaStream(tracks) as MeetingRecordingStream;

  mixed.stopCapture = () => {
    cancelAnimationFrame(rafId);
    canvasStream.getTracks().forEach((track) => track.stop());
    mixed.getAudioTracks().forEach((track) => track.stop());
  };

  return mixed;
}
